import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import SelectController from "@/common/components/SelectController";
import { useEquipmentManufacturers } from "@/common/hooks/queries/useEquipmentManufacturers";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { Control, FieldErrors } from "react-hook-form";
import { useParams } from "react-router-dom";
import { CreateWorkflowSchedulesSchema } from "../services/workflowSchedules.schema";
import { useEquipmentCategories } from "@/features/equipments/hooks/useEquipmentCategories";
import { CompanyEquipmentCategoriesTransformer } from "@/common/services/companyEquipmentCategoriesTransformer";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";
import { CompanyBranchesTransformer } from "@/features/companyBranches/services/companyBranchesTransformer";
import { useEquipments } from "@/features/equipments/hooks/useEquipments";
import { EquipmentsTransformer } from "@/features/equipments/services/equipmentTransformer";
import { useEquipmentModels } from "@/features/equipments/hooks/useEquipmentModels";
import { EquipmentManufacturersTransformer } from "@/common/services/equipmentManufacturersTransformer";
import { EquipmentModelsTransformer } from "@/common/services/equipmentModelsTransformer";
import { ErrorMessage } from "@/common/components/ErrorMessage";

interface AccordionMaintenanceWorkflowSchedulesEquipmentsProps {
  errors: FieldErrors<CreateWorkflowSchedulesSchema>
  control: Control<CreateWorkflowSchedulesSchema>
  disabled?: boolean
}

export default function AccordionMaintenanceWorkflowSchedulesEquipments({ errors, control, disabled }: AccordionMaintenanceWorkflowSchedulesEquipmentsProps) {
  const { companyId } = useParams()

  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')
  const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('')
  const [searchCompanyEquipments, setSearchCompanyEquipments] = useDebounce('')
  const [searchEquipmentManufacturers, setSearchEquipmentManufacturers] = useDebounce('')
  const [searchEquipmentModels, setSearchEquipmentModels] = useDebounce('')

  const { data: equipmentCategoryOptions, isPending: equipmentCategoryOptionsIsPending } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapWithTransformer({ transformer: CompanyEquipmentCategoriesTransformer.optionTransformer }),
  })

  const { data: companyBranchesOptions, isPending: companyBranchesOptionsIsPending } = useAvailableCompanyBranches({
    filters: { search: searchCompanyBranch },
    mapper: Mapper.mapWithTransformer({ transformer: CompanyBranchesTransformer.optionsTransformer() }),
  })

  const { data: equipmentsOptions, isPending: equipmentsOptionsIsPending } = useEquipments({
    filters: { search: searchCompanyEquipments, companyId },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentsTransformer.optionsTransformer() }),
  })

  const { data: equipmentManufacturersOptions, isPending: equipmentManufacturersOptionsIsPending } = useEquipmentManufacturers({
    filters: { search: searchEquipmentManufacturers },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentManufacturersTransformer.optionsTransformer() }),
  })

  const { data: equipmentModelsOptions, isPending: equipmentModelsOptionsIsPending } = useEquipmentModels({
    filters: { search: searchEquipmentModels },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentModelsTransformer.optionsTransformer() }),
  })

  return (
    <Accordion
      name="workflowSchedulesEquipments"
      head={
        <div className="flex w-full flex-col items-start gap-y-1">
          <p>
            Equipamentos {!disabled && <span className="text-danger-soft"> *</span>}
          </p>

          {!!errors.equipment_category_ids?.message && (
            <ErrorMessage name="objectives_attributes" message={'É nescessário pelo menos uma opção de agrupamento de equipamentos'} />
          )}
        </div>
      }
      body={
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <SelectController
              control={control}
              name="equipment_category_ids"
              label="Categoria de equipamento"
              onInputChange={setSearchEquipmentCategory}
              options={equipmentCategoryOptions || []}
              errorMessage={errors?.equipment_category_ids?.message as string}
              isDisabled={equipmentCategoryOptionsIsPending || disabled}
              optionLabelFormat="icon"
            />
            <SelectController
              control={control}
              name="branch_ids"
              label="Filiais"
              onInputChange={setSearchCompanyBranch}
              options={companyBranchesOptions || []}
              isMulti
              errorMessage={errors?.branch_ids?.message as string}
              isDisabled={companyBranchesOptionsIsPending || disabled}
              optionLabelFormat="icon"
            />
            <Box className="flex flex-col gap-4 bg-white-100 p-4">
              <SelectController
                control={control}
                name="manufacturer_ids"
                label="Fabricantes"
                onInputChange={setSearchEquipmentManufacturers}
                options={equipmentManufacturersOptions || []}
                isMulti
                errorMessage={errors?.manufacturer_ids?.message as string}
                isDisabled={equipmentManufacturersOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
              <SelectController
                control={control}
                name="equipment_model_ids"
                label="Modelos"
                onInputChange={setSearchEquipmentModels}
                options={equipmentModelsOptions || []}
                isMulti
                errorMessage={errors?.equipment_model_ids?.message as string}
                isDisabled={equipmentModelsOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
              <SelectController
                control={control}
                name="equipment_ids"
                label="Equipamentos na frota"
                onInputChange={setSearchCompanyEquipments}
                options={equipmentsOptions || []}
                isMulti
                errorMessage={errors?.equipment_ids?.message as string}
                isDisabled={equipmentsOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
            </Box>
          </div>
        </div>
      }
    />
  )
}
